import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AgentIcon from '../../components/picture-ai/AgentIcon';
import ProgramsSummary from '../../components/picture-ai/ProgramsSummary';
import { agentPresentation } from './agents';
import { buttonClass, cardClass, formatCost, ghostButtonClass, useBusyMessage, usePictureAiApi } from './usePictureAi';

const questionScopeLabel = (configuration, t) => {
  const count = configuration.selected_questions.length;
  if (configuration.question_scope === 'selected') return t('{{count}} question(s)', { count });
  if (configuration.question_scope === 'excluded') return t('all questions but {{count}}', { count });
  return t('all questions');
};

const StateBadge = ({ configuration }) => {
  const { t } = useTranslation();
  if (configuration.archived_at) return <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">{t('Archived')}</span>;
  if (configuration.enabled) return <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-900 dark:bg-green-950 dark:text-green-100">{t('Enabled')}</span>;
  return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">{t('Disabled')}</span>;
};

const ConfigurationList = ({ fetchWithAuth }) => {
  const { t } = useTranslation();
  const request = usePictureAiApi(fetchWithAuth);
  const { busy, message, run } = useBusyMessage();
  const [configurations, setConfigurations] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);


  const refresh = useCallback(() => request(`/admin/configurations?include_archived=${includeArchived}`)
    .then(setConfigurations)
    .catch(() => setConfigurations([])), [request, includeArchived]);

  useEffect(() => { refresh(); }, [refresh]);

  // Archiving, never deleting: the analyses a configuration produced stay readable afterwards.
  const archive = (configuration) => run(
    async () => { await request(`/admin/configurations/${configuration.id}`, { method: 'DELETE' }); await refresh(); },
    () => t('“{{name}}” archived. Its analyses are kept.', { name: configuration.name })
  );

  const restore = (configuration) => run(
    async () => { await request(`/admin/configurations/${configuration.id}/restore`, { method: 'POST' }); await refresh(); },
    () => t('“{{name}}” restored.', { name: configuration.name })
  );

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('AI analysis configurations')}</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('One agent, the programs it runs on, and the questions it targets.')}</p>
        </div>
        <Link className={buttonClass} to="/admin/ai/configurations/new">{t('New configuration')}</Link>
      </header>

      {message && (
        <p className={`rounded-lg p-3 text-sm ${message.tone === 'error' ? 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200' : 'bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-200'}`}>
          {message.text}
        </p>
      )}

      <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        <input type="checkbox" checked={includeArchived} onChange={event => setIncludeArchived(event.target.checked)} />
        {t('Show archived configurations')}
      </label>

      {configurations.length === 0 && (
        <div className={cardClass}>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('No configuration yet. Pick an agent in the marketplace to create one.')}</p>
        </div>
      )}

      <div className="space-y-3">
        {configurations.map((configuration) => {
          const agent = agentPresentation(configuration.agent_key);
          return (
          <article
            key={configuration.id}
            className={cardClass}
            style={{ borderLeftWidth: 4, borderLeftColor: agent.color }}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <AgentIcon agent={agent} size={36} />
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{configuration.name}</h2>
                    <StateBadge configuration={configuration} />
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <span style={{ color: agent.color }}>{t(agent.name)}</span>
                  {configuration.active_version && ` · ${t('version {{version}}', { version: configuration.active_version })}`}
                  {configuration.model && ` · ${configuration.model}`}
                    {` · ${questionScopeLabel(configuration, t)}`}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Link className={ghostButtonClass} to={`/admin/ai/configurations/${configuration.id}`}>{t('Edit')}</Link>
                {configuration.archived_at
                  ? <button className={ghostButtonClass} disabled={busy} onClick={() => restore(configuration)}>{t('Restore')}</button>
                  : <button className={ghostButtonClass} disabled={busy} onClick={() => archive(configuration)}>{t('Archive')}</button>}
              </div>
            </div>

            <div className="mt-3">
              <ProgramsSummary
                projectkeys={configuration.programs.map(program => program.projectkey)}
                hiddenCount={configuration.hidden_programs_count}
                request={request}
                title={configuration.name}
              />
            </div>

            <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-5">
              {[
                ['Analyzed', configuration.succeeded],
                ['Pending', configuration.pending],
                ['Running', configuration.running],
                ['Failed', configuration.failed],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs uppercase text-gray-500">{t(label)}</dt>
                  <dd className="font-semibold text-gray-900 dark:text-white">{value ?? 0}</dd>
                </div>
              ))}
              <div>
                <dt className="text-xs uppercase text-gray-500">{t('Cost')}</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">{formatCost(configuration.cost_micros)}</dd>
              </div>
            </dl>
          </article>
          );
        })}
      </div>
    </div>
  );
};

export default ConfigurationList;
